/**
 * Theme config.
 *
 * Pre-login (build-time): uses REACT_APP_THEME env var → THEMES catalogue.
 * Post-login: entity theme from DB is applied via buildTheme() + applyTheme().
 *
 * Colors are space-separated R G B integers so Tailwind can apply opacity
 * utilities (e.g. bg-bh-500/50) via rgb(var(--bh-500) / 0.5).
 */

// ── Palette generator — derive 11 shades from a single hex color ─────────────

function _hexToHsl(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: break;
    }
  }
  return [h * 360, s * 100, l * 100];
}

function _hslToRgbStr(h, s, l) {
  h /= 360; s /= 100; l /= 100;
  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const hue = (t) => {
      if (t < 0) t += 1; if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    r = hue(h + 1 / 3); g = hue(h); b = hue(h - 1 / 3);
  }
  return `${Math.round(r * 255)} ${Math.round(g * 255)} ${Math.round(b * 255)}`;
}

// Lightness / saturation table (approximates Tailwind's perceptual ramp)
const _SHADE_SCALE = [
  [  50, 97, 20],
  [ 100, 93, 35],
  [ 200, 85, 55],
  [ 300, 75, 68],
  [ 400, 63, 78],
  [ 500, 52, 83],
  [ 600, 42, 83],
  [ 700, 34, 78],
  [ 800, 27, 72],
  [ 900, 21, 65],
  [ 950, 14, 58],
];

export function generatePalette(hex) {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) hex = "#3b82f6";
  const [h] = _hexToHsl(hex);
  return Object.fromEntries(
    _SHADE_SCALE.map(([shade, l, s]) => [shade, _hslToRgbStr(h, s, l)])
  );
}

// ── Powered-by block ──────────────────────────────────────────────────────────

const BRUMBOO = {
  logo:  "/logos/brumboo-logo.svg",
  alt:   "brumboo",
  href:  "https://brumboo.com",
  label: "powered by",
};

// ── Theme catalogue (build-time / pre-login) ──────────────────────────────────

const THEMES = {
  fastblade: {
    appName:    "FastBlade",
    themeColor: "#3b82f6",
    favicon:    "/logos/fastblade_logo.png",
    colors:     generatePalette("#3b82f6"),
    headerLogo: { src: "/logos/fastblade_logo.png", alt: "FastBlade", className: "h-6 w-auto" },
    poweredBy:  BRUMBOO,
  },
};

// ── Build a full theme object from entity.theme stored in the DB ──────────────

export function buildTheme(entityTheme) {
  if (!entityTheme || Object.keys(entityTheme).length === 0) return THEMES.fastblade;
  return {
    appName:    entityTheme.app_name    ?? "FastBlade",
    themeColor: entityTheme.theme_color ?? "#3b82f6",
    favicon:    entityTheme.logo_url    ?? "/logos/fastblade_logo.png",
    colors:     generatePalette(entityTheme.theme_color),
    headerLogo: entityTheme.logo_url
      ? { src: entityTheme.logo_url, alt: entityTheme.app_name ?? "Logo", className: "h-6 w-auto" }
      : THEMES.fastblade.headerLogo,
    poweredBy:  BRUMBOO,
  };
}

// ── Apply a theme object to the DOM (CSS vars + metadata) ────────────────────

export function applyTheme(t) {
  if (!t) return;
  const root = document.documentElement;
  Object.entries(t.colors).forEach(([shade, rgb]) => {
    root.style.setProperty(`--bh-${shade}`, rgb);
  });
  document.title = t.appName;
  const metaColor = document.querySelector('meta[name="theme-color"]');
  if (metaColor) metaColor.setAttribute("content", t.themeColor);
  const favicon = document.querySelector('link[rel="icon"]');
  if (favicon) favicon.setAttribute("href", t.favicon);
}

// ── Active theme (static, for pre-login / build-time use) ────────────────────

const key = process.env.REACT_APP_THEME || "fastblade";
const theme = THEMES[key] || THEMES.fastblade;

export { THEMES };
export default theme;
